import React from "react";
import styled from "styled-components";
import Alert from "components/ui/Alert";
import { OnboardingState } from "types/onboarding";
import { PartnerSlug, ProductName } from "types/general";
import { ApiAdminFunnelUser } from "utils/api/admin/user";
import {
  HIDDEN_ONBOARDING_STATES_EWA,
  ONBOARDING_STATES_EWA,
  ONBOARDING_STATES_TO_DISPLAY_EWA,
  HIDDEN_ONBOARDING_STATES_TAX,
  ONBOARDING_STATES_TAX,
  ONBOARDING_STATES_TO_DISPLAY_TAX,
} from "./constants";
import {
  onboardingStateCountCumulativeReducer,
  onboardingStateCountReducer,
} from "./utils";
import { BarChart } from "../Chart";

const Wrapper = styled.div``;

interface Props {
  productEntryPoint: ProductName;
  series: any;
  users: ApiAdminFunnelUser[];
  sortByKey: string;
}

const OnboardingFunnel: React.FC<Props> = ({
  productEntryPoint,
  series,
  users,
  sortByKey,
}) => {
  const keys = series.map((s) => s.dataKey);
  const hiddenOnboardingStates =
    productEntryPoint === ProductName.taxRefundAdvance
      ? HIDDEN_ONBOARDING_STATES_TAX
      : HIDDEN_ONBOARDING_STATES_EWA;
  const onboardingStates =
    productEntryPoint === ProductName.taxRefundAdvance
      ? ONBOARDING_STATES_TAX
      : ONBOARDING_STATES_EWA;
  const onboardingStatesToDisplay =
    productEntryPoint === ProductName.taxRefundAdvance
      ? ONBOARDING_STATES_TO_DISPLAY_TAX
      : ONBOARDING_STATES_TO_DISPLAY_EWA;

  const missingStates = new Set<OnboardingState>(); // This allows us to display an alert if any onboarding states are unaccounted for
  const onboardingStateCountBySlug = users.reduce(
    onboardingStateCountReducer({
      missingStates,
      productEntryPoint,
      sortByKey,
    }),
    Array(onboardingStates.length).fill({}) as {
      [key in PartnerSlug | "all"]: number;
    }[]
  );

  // We had a couple of users who were not yet eligible make it through to accepting terms
  const ineligibleUsersWhoAcceptedTerms = users.filter(
    (user) =>
      !user.eligible &&
      user.onboardingState === OnboardingState.acceptedTermsAndConditions
  );
  const onboardedEligibleAdjustment = keys.reduce(
    (acc, key) => {
      const count = acc[key];
      return {
        ...acc,
        [key]:
          count -
          ineligibleUsersWhoAcceptedTerms.filter(
            (user) => user[sortByKey] === key
          ).length,
      };
    },
    keys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})
  );

  const onboardingStateCountBySlugCumulative = onboardingStateCountBySlug
    .slice()
    .reverse()
    .reduce(
      onboardingStateCountCumulativeReducer({
        productEntryPoint,
        keys,
        onboardedEligibleAdjustment,
      }),
      []
    );

  return (
    <Wrapper>
      {missingStates.size > 0 && (
        <Alert>
          Some onboarding states may be unaccounted for:{" "}
          {Array.from(missingStates)
            .map((s) => s)
            .join(", ")}
        </Alert>
      )}

      <BarChart
        dataset={onboardingStateCountBySlugCumulative.filter(
          (_, i) => !hiddenOnboardingStates.has(onboardingStates[i])
        )}
        series={series}
        xAxis={[
          {
            data: onboardingStatesToDisplay,
            scaleType: "band",
          },
        ]}
        height={400}
        barLabel={series.length === 1 ? "value" : undefined}
      />
    </Wrapper>
  );
};

export default OnboardingFunnel;
